import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Avatar, Box, Chip, CircularProgress, IconButton, Link, Skeleton, Tooltip, Typography } from '@mui/material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import RefreshIcon from '@mui/icons-material/Refresh';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LogoutIcon from '@mui/icons-material/Logout';
import KeyboardOutlinedIcon from '@mui/icons-material/KeyboardOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import Modal from '../../../components/Modal/Modal';
import { AppButton } from '../../../components/UI';
import { EXTENSION_FOLDER, openRealLinkedin, useLinkedin } from '../extensionBridge';
import {
  LINKEDIN_CHANGED_EVENT, cancelLinkedinLogin, disconnectLinkedin, fetchLinkedinAccount, fetchLinkedinLoginScreen,
  finishLinkedinLogin, linkedinLoginClick, linkedinLoginKey, linkedinLoginState, linkedinLoginType, notifyLinkedinChanged,
  startLinkedinLogin,
} from '../api';

const SPECIAL_KEYS = new Set(['Enter', 'Backspace', 'Tab', 'Delete', 'Escape', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']);
const SCREEN_MS = 700;
const STATE_MS = 1500;
export const LINKEDIN_LOGIN_PATH = '/sourcing/connexion-linkedin';
const CHANNEL = 'sourcing-linkedin';

/** Opens the LinkedIn login page of the CRM in a new browser tab. */
export const openLinkedinLoginTab = () => window.open(LINKEDIN_LOGIN_PATH, '_blank');

/** Tells the other CRM tabs that the LinkedIn connection changed. */
const broadcastLinkedinChanged = () => {
  notifyLinkedinChanged();
  try {
    const channel = new BroadcastChannel(CHANNEL);
    channel.postMessage('changed');
    channel.close();
  } catch (e) { /* BroadcastChannel unavailable */ }
};

/** Account used by the server for collection; refreshed when the login tab finishes. */
export function useLinkedinAccount() {
  const [state, setState] = useState({ loading: true, status: null, account: null, detail: '' });

  const refresh = useCallback(() => {
    setState((s) => ({ ...s, loading: true }));
    fetchLinkedinAccount()
      .then((d) => setState({ loading: false, status: d.status, account: d.account || null, detail: d.detail || '' }))
      .catch((e) => setState({ loading: false, status: 'error', account: null, detail: e.message }));
  }, []);

  useEffect(() => {
    refresh();
    window.addEventListener(LINKEDIN_CHANGED_EVENT, refresh);
    let channel;
    try {
      channel = new BroadcastChannel(CHANNEL);
      channel.onmessage = refresh;
    } catch (e) { /* BroadcastChannel unavailable */ }
    return () => {
      window.removeEventListener(LINKEDIN_CHANGED_EVENT, refresh);
      channel?.close();
    };
  }, [refresh]);

  return { ...state, refresh };
}

/**
 * LinkedIn's real login page, opened by the sourcing server and shown live:
 * the user clicks and types in it as in a normal page (password, 2FA, captcha).
 */
export const LinkedinLoginView = ({ onConnected }) => {
  const [session, setSession] = useState(null);
  const [image, setImage] = useState(null);
  const [phase, setPhase] = useState('starting'); // starting | ready | saving | done | error
  const [error, setError] = useState('');
  const [account, setAccount] = useState(null);
  const [focused, setFocused] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const surfaceRef = useRef(null);
  const queue = useRef(Promise.resolve());
  const sessionRef = useRef(null);
  sessionRef.current = session;

  // Keeps keystrokes in order: each call waits for the previous one.
  const send = useCallback((fn) => {
    queue.current = queue.current.then(fn).catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;
    setPhase('starting');
    setError('');
    setImage(null);
    startLinkedinLogin()
      .then((s) => {
        if (!alive) { cancelLinkedinLogin(s.id); return; }
        setSession(s);
        setPhase('ready');
        setTimeout(() => surfaceRef.current?.focus(), 300);
      })
      .catch((e) => { if (alive) { setPhase('error'); setError(e.message); } });
    return () => {
      alive = false;
      if (sessionRef.current) cancelLinkedinLogin(sessionRef.current.id);
      setSession(null);
    };
  }, [attempt]);

  // Closing the tab cancels the login on the server.
  useEffect(() => {
    const onUnload = () => {
      if (sessionRef.current) cancelLinkedinLogin(sessionRef.current.id);
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);

  // Live screen.
  useEffect(() => {
    if (!session || phase !== 'ready') return undefined;
    let alive = true;
    let timer;
    let url;
    const tick = async () => {
      try {
        const blob = await fetchLinkedinLoginScreen(session.id);
        if (blob && alive) {
          const next = URL.createObjectURL(blob);
          setImage(next);
          if (url) URL.revokeObjectURL(url);
          url = next;
        }
      } catch (e) {
        if (alive) { setPhase('error'); setError('La page de connexion a expiré. Recommencez.'); }
        return;
      }
      if (alive) timer = setTimeout(tick, SCREEN_MS);
    };
    tick();
    return () => { alive = false; clearTimeout(timer); if (url) URL.revokeObjectURL(url); };
  }, [session, phase]);

  // Detects the end of the login and saves the session.
  useEffect(() => {
    if (!session || phase !== 'ready') return undefined;
    const id = setInterval(async () => {
      try {
        const st = await linkedinLoginState(session.id);
        if (!st.logged_in) return;
        clearInterval(id);
        setPhase('saving');
        await finishLinkedinLogin(session.id);
        setSession(null);
        const info = await fetchLinkedinAccount().catch(() => ({}));
        setAccount(info.account || null);
        setPhase('done');
        broadcastLinkedinChanged();
        onConnected?.(info.account || null);
      } catch (e) { /* next tick */ }
    }, STATE_MS);
    return () => clearInterval(id);
  }, [session, phase, onConnected]);

  const onClick = (e) => {
    if (!session) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * session.width;
    const y = ((e.clientY - rect.top) / rect.height) * session.height;
    send(() => linkedinLoginClick(session.id, x, y));
    surfaceRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (!session || e.ctrlKey || e.metaKey || e.altKey) return;
    if (SPECIAL_KEYS.has(e.key)) {
      e.preventDefault();
      send(() => linkedinLoginKey(session.id, e.key));
    } else if (e.key.length === 1) {
      e.preventDefault();
      send(() => linkedinLoginType(session.id, e.key));
    }
  };

  const onPaste = (e) => {
    const text = e.clipboardData?.getData('text');
    if (session && text) {
      e.preventDefault();
      send(() => linkedinLoginType(session.id, text));
    }
  };

  if (phase === 'done') {
    return (
      <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5 }}>
        <Avatar src={account?.picture_url || undefined} sx={{ width: 72, height: 72, bgcolor: '#e8f1fb', color: '#0a66c2' }}>
          <CheckCircleIcon sx={{ fontSize: 40, color: '#16a34a' }} />
        </Avatar>
        <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: '#0f172a' }}>LinkedIn connecté{account?.name ? ` : ${account.name}` : ''}</Typography>
        <Typography sx={{ fontSize: '0.86rem', color: '#64748b' }}>
          Le sourcing utilise maintenant ce compte. Cet onglet va se fermer ; vous pouvez revenir sur la page Sourcing.
        </Typography>
        <AppButton onClick={() => window.close()}>Fermer cet onglet</AppButton>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25, color: focused ? '#03407b' : '#64748b' }}>
        <KeyboardOutlinedIcon sx={{ fontSize: 18 }} />
        <Typography sx={{ fontSize: '0.8rem', fontWeight: focused ? 700 : 500 }}>
          {focused ? 'Clavier actif : tapez directement, Entrée pour valider (Ctrl+V pour coller).' : 'Cliquez dans un champ de la page, puis tapez au clavier.'}
        </Typography>
      </Box>
      <Box
        ref={surfaceRef}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        sx={{
          position: 'relative',
          mx: 'auto',
          maxWidth: 1000,
          borderRadius: '12px',
          overflow: 'hidden',
          border: `2px solid ${focused ? '#0a66c2' : '#e2e8f0'}`,
          bgcolor: '#f8fafc',
          aspectRatio: session ? `${session.width} / ${session.height}` : '1000 / 720',
          outline: 'none',
          transition: 'border-color .15s',
        }}
      >
        {image && (
          <Box component="img" src={image} alt="Page de connexion LinkedIn" onClick={onClick} draggable={false} sx={{ display: 'block', width: '100%', height: '100%', cursor: 'pointer', userSelect: 'none' }} />
        )}
        {(phase === 'starting' || (phase === 'ready' && !image) || phase === 'saving' || phase === 'error') && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.25, bgcolor: image ? 'rgba(255,255,255,0.85)' : 'transparent', textAlign: 'center', px: 3 }}>
            {phase !== 'error' && <CircularProgress size={32} sx={{ color: '#0a66c2' }} />}
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: phase === 'error' ? '#dc2626' : '#0f172a' }}>
              {{ starting: 'Ouverture de LinkedIn… (quelques secondes)', ready: 'Chargement de la page…', saving: 'Connexion réussie, enregistrement…' }[phase] || error}
            </Typography>
            {phase === 'error' && <AppButton size="small" startIcon={<RefreshIcon />} onClick={() => setAttempt((a) => a + 1)}>Recommencer</AppButton>}
          </Box>
        )}
      </Box>
      <Typography sx={{ mt: 1.25, fontSize: '0.74rem', color: '#94a3b8', lineHeight: 1.5, textAlign: 'center' }}>
        La page affichée est celle de LinkedIn, ouverte par le serveur de sourcing. Vos identifiants sont saisis directement chez LinkedIn :
        ils ne sont ni enregistrés ni conservés. Seule la session est gardée pour la collecte.
      </Typography>
    </Box>
  );
};

const STATUS = {
  connected: { label: 'Connecté', bg: '#f0fdf4', color: '#16a34a' },
  expired: { label: 'Session expirée', bg: '#fffbeb', color: '#b45309' },
  missing: { label: 'Non connecté', bg: '#fffbeb', color: '#b45309' },
  unknown: { label: 'Non vérifié', bg: '#f1f5f9', color: '#64748b' },
  error: { label: 'Service injoignable', bg: '#fef2f2', color: '#dc2626' },
};

/** Server session card (no extension): connect / change / disconnect through the remote login tab. */
const ServerAccountCard = ({ snackbarShowMessage, compact = false, onInstall }) => {
  const { loading, status, account, detail, refresh } = useLinkedinAccount();
  const [disconnecting, setDisconnecting] = useState(false);

  if (loading && !status) {
    return <Skeleton variant="rounded" height={compact ? 44 : 64} sx={{ borderRadius: '12px', minWidth: 260 }} />;
  }

  const st = STATUS[status] || STATUS.unknown;
  const connected = status === 'connected';

  const disconnect = async () => {
    setDisconnecting(true);
    try {
      await disconnectLinkedin();
      broadcastLinkedinChanged();
      snackbarShowMessage?.('LinkedIn déconnecté du sourcing.', 'success');
    } catch (e) {
      snackbarShowMessage?.(e.message, 'error');
    } finally {
      setDisconnecting(false);
    }
  };

  const name = connected
    ? account?.name || 'LinkedIn connecté'
    : status === 'expired' ? 'Session LinkedIn expirée' : status === 'error' ? 'Sourcing injoignable' : 'LinkedIn non connecté';
  const sub = connected
    ? `${account?.headline ? `${account.headline} · ` : ''}Compte utilisé pour collecter profils et offres`
    : status === 'error' ? detail || 'Vérifiez que le service de sourcing tourne'
      : 'Connectez un compte LinkedIn pour lancer des imports.';

  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: compact ? 'nowrap' : 'wrap',
        p: compact ? 0.75 : 1.5, borderRadius: '12px',
        border: `1px solid ${connected ? '#e2e8f0' : '#fde68a'}`,
        bgcolor: connected ? '#fff' : '#fffbeb',
      }}
    >
      <Avatar src={account?.picture_url || undefined} sx={{ width: compact ? 32 : 40, height: compact ? 32 : 40, bgcolor: '#e8f1fb', color: '#0a66c2' }}>
        <LinkedInIcon fontSize="small" />
      </Avatar>
      <Box sx={{ flex: 1, minWidth: compact ? 0 : 220 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          {connected && account?.profile_url ? (
            <Link href={account.profile_url} target="_blank" rel="noopener noreferrer" underline="hover" noWrap sx={{ fontSize: '0.86rem', fontWeight: 800, color: '#0f172a' }}>{name}</Link>
          ) : (
            <Typography noWrap sx={{ fontSize: '0.86rem', fontWeight: 800, color: '#0f172a' }}>{name}</Typography>
          )}
          <Chip size="small" label={st.label} sx={{ height: 20, fontSize: '0.66rem', fontWeight: 700, bgcolor: st.bg, color: st.color, flexShrink: 0 }} />
        </Box>
        {!compact && <Typography sx={{ fontSize: '0.76rem', color: '#64748b' }}>{sub}</Typography>}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
        {onInstall && (
          <Tooltip title="Avec l'extension, connectez-vous sur le vrai linkedin.com : le compte est détecté automatiquement.">
            <span>
              <AppButton size="small" variant="secondary" startIcon={<ExtensionOutlinedIcon />} onClick={onInstall}>
                {compact ? 'Extension' : "Installer l'extension"}
              </AppButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Vérifier la session">
          <span>
            <IconButton size="small" onClick={refresh} disabled={loading} sx={{ border: '1px solid #e2e8f0', borderRadius: '8px' }}>
              {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        {connected && !compact && (
          <Tooltip title="Déconnecter ce compte du sourcing">
            <span>
              <IconButton size="small" onClick={disconnect} disabled={disconnecting} sx={{ border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626' }}>
                <LogoutIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <AppButton
          size="small"
          variant={connected ? 'outline' : 'primary'}
          startIcon={connected ? <SwapHorizIcon /> : <LinkedInIcon />}
          disabled={status === 'error'}
          onClick={openLinkedinLoginTab}
        >
          {connected ? 'Changer de compte' : 'Connecter LinkedIn'}
        </AppButton>
      </Box>
    </Box>
  );
};

const INSTALL_STEPS = [
  <>Ouvrez <b>chrome://extensions</b> (ou edge://extensions) dans un nouvel onglet.</>,
  <>Activez le <b>Mode développeur</b> (en haut à droite).</>,
  <>Cliquez sur <b>Charger l'extension non empaquetée</b> et choisissez le dossier <b>{EXTENSION_FOLDER}</b>. Si elle est déjà installée, cliquez sur son bouton <b>Recharger</b>.</>,
  <>Revenez ici et rechargez la page : votre compte LinkedIn apparaît automatiquement.</>,
];

export const InstallExtensionModal = ({ open, onClose }) => (
  <Modal
    open={open}
    onClose={onClose}
    maxWidth="sm"
    title={
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{ width: 38, height: 38, borderRadius: '10px', bgcolor: '#ebf4fb', color: '#03407b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ExtensionOutlinedIcon />
        </Box>
        <Box>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>Installer l'extension Sourcing</Typography>
          <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>Une seule fois : ensuite vous vous connectez sur le vrai linkedin.com.</Typography>
        </Box>
      </Box>
    }
    actions={
      <>
        <AppButton variant="secondary" onClick={onClose}>Fermer</AppButton>
        <AppButton startIcon={<RefreshIcon />} onClick={() => window.location.reload()}>Recharger la page</AppButton>
      </>
    }
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {INSTALL_STEPS.map((step, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
          <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: '#03407b', color: '#fff', fontSize: '0.74rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</Box>
          <Typography sx={{ fontSize: '0.84rem', color: '#334155', lineHeight: 1.55 }}>{step}</Typography>
        </Box>
      ))}
      <Box sx={{ p: 1.25, borderRadius: '10px', bgcolor: '#f0f7fd', border: '1px solid #cfe0f1' }}>
        <Typography sx={{ fontSize: '0.76rem', color: '#334155', lineHeight: 1.5 }}>
          L'extension utilise le LinkedIn connecté dans ce navigateur (mots de passe enregistrés, compte Google…). Les profils sont lus ici
          et seul leur texte est envoyé au sourcing : votre session LinkedIn ne quitte jamais Chrome.
        </Typography>
      </Box>
    </Box>
  </Modal>
);

/** Card for the LinkedIn account of this browser, read by the extension. */
const BrowserAccountCard = ({ li, compact }) => {
  const connected = li.connected;
  const chip = connected ? { label: 'Connecté', bg: '#f0fdf4', color: '#16a34a' } : { label: 'Non connecté', bg: '#fffbeb', color: '#b45309' };
  const name = connected ? li.account?.name || 'LinkedIn connecté' : 'LinkedIn non connecté';
  const headline = li.account?.headline ? `${li.account.headline} · ` : '';
  const sub = connected
    ? `${headline}Compte LinkedIn de ce navigateur, utilisé pour la collecte`
    : 'Connectez-vous sur linkedin.com : votre compte sera détecté automatiquement.';
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: compact ? 'nowrap' : 'wrap',
        p: compact ? 0.75 : 1.5, borderRadius: '12px',
        border: `1px solid ${connected ? '#e2e8f0' : '#fde68a'}`,
        bgcolor: connected ? '#fff' : '#fffbeb',
      }}
    >
      <Avatar src={li.account?.picture_url || undefined} sx={{ width: compact ? 32 : 40, height: compact ? 32 : 40, bgcolor: '#e8f1fb', color: '#0a66c2' }}>
        <LinkedInIcon fontSize="small" />
      </Avatar>
      <Box sx={{ flex: 1, minWidth: compact ? 0 : 220 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          {connected && li.account?.profile_url ? (
            <Link href={li.account.profile_url} target="_blank" rel="noopener noreferrer" underline="hover" noWrap sx={{ fontSize: '0.86rem', fontWeight: 800, color: '#0f172a' }}>{name}</Link>
          ) : (
            <Typography noWrap sx={{ fontSize: '0.86rem', fontWeight: 800, color: '#0f172a' }}>{name}</Typography>
          )}
          <Chip size="small" label={chip.label} sx={{ height: 20, fontSize: '0.66rem', fontWeight: 700, bgcolor: chip.bg, color: chip.color, flexShrink: 0 }} />
        </Box>
        {!compact && <Typography sx={{ fontSize: '0.76rem', color: '#64748b' }}>{sub}</Typography>}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
        <Tooltip title="Actualiser">
          <IconButton size="small" onClick={li.refresh} sx={{ border: '1px solid #e2e8f0', borderRadius: '8px' }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {connected ? (
          <Tooltip title="Déconnectez-vous de LinkedIn sur linkedin.com puis connectez l'autre compte : il sera détecté automatiquement.">
            <span><AppButton size="small" variant="outline" startIcon={<SwapHorizIcon />} onClick={openRealLinkedin}>Changer de compte</AppButton></span>
          </Tooltip>
        ) : (
          <AppButton size="small" startIcon={<LinkedInIcon />} onClick={openRealLinkedin}>Connecter LinkedIn</AppButton>
        )}
      </Box>
    </Box>
  );
};

/** LinkedIn account used by the collection: this browser's account with the extension, else the server session. */
const LinkedinAccount = ({ snackbarShowMessage, compact = false }) => {
  const li = useLinkedin();
  const [installOpen, setInstallOpen] = useState(false);

  if (!li || li.checking) {
    return <Skeleton variant="rounded" height={compact ? 44 : 64} sx={{ borderRadius: '12px', minWidth: 260 }} />;
  }
  if (li.installed) return <BrowserAccountCard li={li} compact={compact} />;
  return (
    <>
      <ServerAccountCard snackbarShowMessage={snackbarShowMessage} compact={compact} onInstall={() => setInstallOpen(true)} />
      <InstallExtensionModal open={installOpen} onClose={() => setInstallOpen(false)} />
    </>
  );
};

export default LinkedinAccount;
